import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Clickable from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import { LinkProps } from "@insite/mobius/Link";
import Tab, { TabProps } from "@insite/mobius/Tab";
import TabGroup from "@insite/mobius/TabGroup";
import { resolveColor } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import parse from "html-react-parser";
import { css, FlattenSimpleInterpolation } from "styled-components";

interface AboutModel {
    fields: {
        title: string;
        content: string;
    };
}

const enum fields {
    abouts = "abouts",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.abouts]: AboutModel[];
    };
    extendedStyles?: AboutSectionStyles;
}

export interface AboutSectionStyles {
    container?: InjectableCss;
    accordionSection?: AccordionSectionPresentationProps;
    accordion?: AccordionProps;
}

export const aboutSectionStyles: AboutSectionStyles = {
    accordionSection: {
        headerProps: {
            css: css`
                border-radius: 5px;
                margin-bottom: 10px;
                & button {
                    background: ${COLORS.white};
                    border-radius: 5px;
                }
            `,
        },
        titleTypographyProps: {
            size: TYPOGRAPHY.size.small,
        },
        panelProps: {
            css: css`
                background: ${COLORS.white};
                border: none;
                margin-top: -10px;
            `,
        },
    },
    accordion: {
        headingLevel: 2,
        css: css`
            margin-top: 20px;
        `,
    },
    container: {
        css: css`
            padding: 35px 0;
            border-radius: 10px;
            background-color: ${COLORS.white};
        `,
    },
};

const mapDispatchToProps = {};
type Props = OwnProps;

const tabCss: FlattenSimpleInterpolation = css`
    &:hover {
        cursor: pointer;
        border-bottom: 1px solid ${getColor("primary")};
        border-top: 1px solid ${getColor("primary")};
    }
    border-bottom: 1px solid
        ${({ selected, theme }: TabProps) => (selected ? resolveColor("primary", theme!) : "transparent")};
    border-top: 1px solid
        ${({ selected, theme }: TabProps) => (selected ? resolveColor("primary", theme!) : "transparent")};
    transition: all 0.4s;
    &:focus {
        outline: none;
    }
    font-weight: ${({ selected }: TabProps) => (selected ? "bold" : "normal")};
    padding: 8px 0;
    text-align: left;
    color: ${({ selected, theme }: TabProps) => (selected ? resolveColor("primary", theme!) : "inherit")};
` as FlattenSimpleInterpolation;

const tabContent = css`
    border-bottom: none;
    font-size: ${TYPOGRAPHY.size.small};
    padding: 18px 20px;
    border: none;
`;

const tabGroup = css`
    padding: 0;
    margin-top: 20px;
    max-width: 33%;
    flex-direction: column;
    padding: 0 40px;
`;

const wrapper = css`
    flex-direction: row;
    flex-wrap: wrap;
    & [data-id="tabContent"] {
        max-width: 67%;
    }
`;

const AboutSection: React.FC<Props> = ({ fields, extendedStyles }) => {
    const [styles] = useState(() => mergeToNew(aboutSectionStyles, extendedStyles));
    return (
        <>
            {fields.abouts && fields.abouts?.length > 0 ? (
                <Hidden below="md" as="div">
                    <StyledWrapper {...styles.container}>
                        <TabGroup cssOverrides={{ tabContent, tabGroup, wrapper }}>
                            {fields.abouts?.map((tabItem: any, index: any) => {
                                return (
                                    <Tab css={tabCss} headline={tabItem?.fields.title} tabKey={`tab${index}`}>
                                        {parse(tabItem.fields.content)}
                                    </Tab>
                                );
                            })}
                        </TabGroup>
                    </StyledWrapper>
                </Hidden>
            ) : null}

            {fields.abouts && fields.abouts?.length > 0 ? (
                <Hidden above="sm" as="div">
                    <StyledWrapper>
                        <Accordion headingLevel={2} {...styles.accordion}>
                            {fields.abouts?.map((tabItem: any, index: any) => (
                                <ManagedAccordionSection
                                    key={`tab${index}`}
                                    title={tabItem?.fields.title}
                                    data-test-selector="productDetails_specifications_specification"
                                    {...styles.accordionSection}
                                >
                                    {parse(tabItem.fields.content)}
                                </ManagedAccordionSection>
                            ))}
                        </Accordion>
                    </StyledWrapper>
                </Hidden>
            ) : null}
        </>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: AboutSection,
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.abouts,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "content",
                        editorTemplate: "RichTextField",
                        displayName: "Content",
                        defaultValue: "",
                        extendedConfig: { height: 100 },
                        expandedToolbarButtons: {
                            moreMisc: {},
                            code: {},
                        },
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
